/**
 * tutorLiveCenterHeadParts — ce que montre la tête du centre du live du masteur (pur), lu par
 * `<TutorLiveCenterHead>` ; testé à côté (Unistyles ne se charge pas sous Jest, la règle vit hors du rendu).
 *
 * **La présentation seule** (`presentationOnly`) : l'écran entier part chez les élèves (le natif, ReplayKit /
 * MediaProjection, ou un onglet que le navigateur ne sait pas recadrer). La tête ne garde que ce qui sert
 * la présentation (tech-derisk du câblage, point 3 B : « la pellicule, le fil, le bandeau et les prénoms
 * quittent l'écran ; la barre de dessin et « Arrêter » restent ») : le titre de l'activité, la barre de
 * dessin et « Arrêter » (les onglets sont à part) — ni la ligne d'objectif, ni la bascule du son (le mode
 * d'un élève dit à tout le groupe, et l'élève par contraste), ni le contrôle, ni les gestes d'élève, ni ⋮.
 */

/** Ce que la tête reçoit, réduit à ce qui décide de ses parties. */
export type TutorLiveCenterHeadPartsInput = {
    /** Une activité est ouverte. */
    focus: boolean
    /** Le chapitre et le chrono de l'activité sont donnés. */
    activityInfo: boolean
    /** « Présenter » est offert. */
    present: boolean
    /** La présentation tourne et sa barre de dessin est donnée. */
    toolbar: boolean
    /** « Afficher à l'élève » reste à côté de la barre de dessin. */
    keepShow: boolean
    status: boolean
    primary: boolean
    show: boolean
    mode: boolean
    menu: boolean
    hideIdentity: boolean
    presentationOnly: boolean
}

export type TutorLiveCenterHeadParts = {
    /** La ligne d'objectif (l'élève, ses matières, ce qu'il vise, son contrôle, « Quiz d'abord »). */
    strip: boolean
    /** Dans la ligne : qui est l'élève (visage, prénom, classe, drapeaux), ce qu'il vise, son contrôle. */
    identity: boolean
    /** L'activité en tête des gestes : son titre, son chapitre et son chrono (`full`), son titre seul, ou rien. */
    info: "full" | "title" | null
    /** La bascule Dialogue · Concentré de l'élève regardé. */
    mode: boolean
    /** Le statut de l'activité. */
    status: boolean
    /** L'action qu'attend le statut. */
    primary: boolean
    /** « Afficher à l'élève ». */
    show: boolean
    /** La barre de dessin (et son « Arrêter »), le bouton « Présenter » / « Arrêter », ou rien. */
    present: "toolbar" | "button" | null
    /** ⋮ : les autres gestes sur l'activité. */
    menu: boolean
}

/**
 * Ce que montre la barre flottante du centre (Wajih, 04/11 : la tête empilait trois lignes ; les gestes
 * descendent dans une barre flottante en bas au centre, comme la barre d'outils de Lovable). **Elle suit
 * l'onglet ouvert** : sur une vue de l'activité, ses gestes ; sur la visio ou les contrôles, « Ajouter une
 * activité » seul. Rien ne se perd : ce qu'un onglet ne montre pas reste dans « Plus », ou sur l'onglet où il
 * s'applique.
 *  - l'action qu'attend le statut (Valider, Redonner, Soumettre) porte sur le rendu : elle est en un geste sur
 *    le rendu, le côte à côte et le quiz, et dans « Plus » sur l'énoncé et le tableau blanc ;
 *  - pendant une présentation, la barre se retire : la barre de dessin et « Arrêter » restent dans la tête,
 *    hors de la région partagée — rien ne flotte au-dessus de ce que voient les élèves.
 */
export type TutorLiveActionBarInput = {
    /** La vue du centre : l'onglet ouvert, `split` pour le côte à côte, `null` sans rien à montrer. */
    view: "visio" | "statement" | "work" | "whiteboard" | "quiz" | "split" | "assessments" | "activities" | null
    focus: boolean
    status: boolean
    primary: boolean
    show: boolean
    present: boolean
    /** Une présentation tourne ou part, ou l'écran entier est partagé : la barre se retire. */
    watched: boolean
    /** « Ajouter une activité » est offert (un élève est regardé). */
    add: boolean
}

export type TutorLiveActionBarParts = {
    /** La barre est montrée. */
    shown: boolean
    /** Le titre de l'activité et son chrono, en tête de la barre. */
    info: boolean
    status: boolean
    /** L'action du statut, en un geste ; fausse alors qu'elle est offerte : elle passe dans « Plus ». */
    primary: boolean
    show: boolean
    present: boolean
    /** « Ajouter une activité » en un geste (une vue sans activité) ; sinon il est dans « Plus ». */
    add: boolean
    /** « Plus » : les autres gestes sur l'activité, et « Ajouter une activité ». */
    menu: boolean
}

const PRIMARY_VIEWS = new Set<TutorLiveActionBarInput["view"]>(["work", "split", "quiz"])
const ACTIVITY_VIEWS = new Set<TutorLiveActionBarInput["view"]>(["statement", "work", "whiteboard", "quiz", "split"])

export function tutorLiveActionBarParts(input: TutorLiveActionBarInput): TutorLiveActionBarParts {
    const none = {
        shown: false,
        info: false,
        status: false,
        primary: false,
        show: false,
        present: false,
        add: false,
        menu: false,
    }
    if (input.watched || input.view == null) return none
    const onActivity = input.focus && ACTIVITY_VIEWS.has(input.view)
    if (!onActivity) return { ...none, shown: input.add, add: input.add }
    const primary = input.primary && PRIMARY_VIEWS.has(input.view)
    return {
        shown: true,
        info: true,
        status: input.status,
        primary,
        show: input.show,
        present: input.present,
        add: false,
        // Toujours là sur une activité : « Ajouter une activité », et l'action du statut quand elle n'est pas en un geste.
        menu: true,
    }
}

export function tutorLiveCenterHeadParts(input: TutorLiveCenterHeadPartsInput): TutorLiveCenterHeadParts {
    const { focus, toolbar } = input
    if (input.presentationOnly) {
        return {
            strip: false,
            identity: false,
            info: focus ? "title" : null,
            mode: false,
            status: false,
            primary: false,
            show: false,
            // « Arrêter » reste toujours à portée, activité ouverte ou non : le groupe voit l'écran entier.
            present: toolbar ? "toolbar" : input.present ? "button" : null,
            menu: false,
        }
    }
    return {
        strip: true,
        identity: !input.hideIdentity,
        // Pendant la barre de dessin, la ligne de l'activité lui cède la place.
        info: focus && input.activityInfo && !toolbar ? "full" : null,
        mode: input.mode,
        status: focus && !toolbar && input.status,
        primary: focus && !toolbar && input.primary,
        show: focus && input.show && (!toolbar || input.keepShow),
        present: toolbar ? "toolbar" : focus && input.present ? "button" : null,
        menu: input.menu,
    }
}
